import { get } from 'lodash-es';

import { getContentfulPageContentForSlug } from '~coreModules/contentful/js/contentful-client';
import { shapeBrowsePageContent } from '~coreModules/contentful/js/contentful-utils';
import { getWhitelistedFilterObject } from '~coreModules/catalog/js/catalog-utils';
import {
    getPreferenceAdjustedQuery,
    getExcludedPageAdjustedQuery,
    getSortAdjustedParameter,
} from '~modules/catalog/js/catalog-utils';
import { getCatalogItems } from '~routes/browse/js/browse-api';

import { SET_404_ERROR } from '~coreModules/core/js/store';
import { AB_TESTING_MODULE_NAME } from '~coreModules/ab-testing/js/ab-testing-store';
import { AB_TEST_BROWSE_PAGE } from '~coreModules/ab-testing/js/ab-testing-constants';
import { CONTENT_TYPES } from '~modules/contentful/js/contentful-constants';

import { CATALOG_TYPES, CATALOG_QUERY_IN_STOCK } from '~modules/catalog/js/catalog-constants';
import { catalogPageNames } from '~modules/core/js/router-constants';
import { CATALOG_MODULE_NAME, getCatalogModule, SET_CATALOG_DATA } from '~modules/catalog/js/catalog-store';
import { SITE_SETTINGS_MODULE_NAME } from '~modules/site-settings/js/site-settings-store';

export const BROWSE_MODULE_NAME = 'browse';

export const FETCH_BROWSE_INFORMATION = 'FETCH_BROWSE_INFORMATION';
export const FETCH_CATALOG_DATA = 'FETCH_CATALOG_DATA';
export const SET_PRODUCT_CLOSET_STATUS = 'SET_PRODUCT_CLOSET_STATUS';
export const SET_BRAND_NAME = 'SET_BRAND_NAME';
export const GET_CONTENTFUL_CONTENT = 'GET_CONTENTFUL_CONTENT';
export const SET_CONTENTFUL_CONTENT = 'SET_CONTENTFUL_CONTENT';
export const SET_IS_PRE_PURCHASE = 'SET_IS_PRE_PURCHASE';
export const SET_SELECTED_CURATION = 'SET_SELECTED_CURATION';
export const FETCH_CURATION_INFORMATION = 'FETCH_CURATION_INFORMATION';

export default {
    namespaced: true,

    state: () => ({
        contentfulContent: {},
        brandName: null,
        isPrePurchase: false,
        selectedCuration: null,
    }),

    /* shared catalog functionality */
    modules: {
        [CATALOG_MODULE_NAME]: getCatalogModule(CATALOG_TYPES.BROWSE),
    },

    /* eslint-disable no-param-reassign */
    mutations: {
        [SET_PRODUCT_CLOSET_STATUS](state, { choiceId, newClosetStatus }) {
            const productIndex = state.catalog.products.findIndex(product => product.choiceId === choiceId);
            state.catalog.products[productIndex].inCloset = newClosetStatus;
        },
        [SET_CONTENTFUL_CONTENT](state, contentfulContent) {
            state.contentfulContent = contentfulContent;
        },
        [SET_BRAND_NAME](state, brandName) {
            state.brandName = brandName;
        },
        [SET_IS_PRE_PURCHASE](state, isPrePurchase) {
            state.isPrePurchase = isPrePurchase;
        },
        [SET_SELECTED_CURATION](state, selectedCuration) {
            state.selectedCuration = selectedCuration;
        },
    },

    /* eslint-enable no-param-reassign */

    actions: {
        [FETCH_BROWSE_INFORMATION]({ dispatch, commit, rootGetters }, {
            slug,
            route,
            brandSlug,
            isPrePurchase = false,
        }) {
            const brandName = get(route, 'query.vendorBrands');

            if (brandName && route.name === catalogPageNames.brand) {
                commit(SET_BRAND_NAME, brandName);
            } else {
                commit(SET_BRAND_NAME, null);
            }

            commit(SET_IS_PRE_PURCHASE, isPrePurchase);

            return dispatch(FETCH_CATALOG_DATA, { slug, query: route.query })
                .then(() => {
                    const browseSlug = rootGetters[`${AB_TESTING_MODULE_NAME}/getSlug`](slug,
                        AB_TEST_BROWSE_PAGE);
                    return dispatch(GET_CONTENTFUL_CONTENT, brandSlug || browseSlug);
                })
                .catch((error) => {
                    this.$logger.debugError('Failed to fetch all browse information: ', error);
                });

        },
        async [FETCH_CURATION_INFORMATION]({ dispatch, commit, rootGetters, state }, {
            slug,
            route,
        }) {
            const browseSlug = rootGetters[`${AB_TESTING_MODULE_NAME}/getSlug`](slug,
                AB_TEST_BROWSE_PAGE);
            const query = route?.query;

            commit(SET_IS_PRE_PURCHASE, false);

            try {
                await dispatch(GET_CONTENTFUL_CONTENT, browseSlug);
                const validCuration =
                    state.contentfulContent?.curationNavItems.find(curation => (
                        curation.slug === route.query?.curation
                    ));
                const sort = validCuration?.sortMethod;
                const adjustedSortParameter = getSortAdjustedParameter(query, sort);

                await dispatch(FETCH_CATALOG_DATA, {
                    slug,
                    ...(validCuration && {
                        query: {
                            ...query,
                            ...adjustedSortParameter,
                        },
                    }),
                });

                return {
                    adjustedSort: adjustedSortParameter?.sort,
                };
            } catch (error) {
                this.$logger.debugError('Failed to fetch curation information: ', error);
                return Promise.reject(error);
            }
        },
        [FETCH_CATALOG_DATA]({ commit, getters, rootState, state }, { slug, query }) {
            const { catalogRequestSettings } = getters;
            const { catalogFilterWhitelist, catalogFilterExcludePageOneWhiteList } = catalogRequestSettings;
            const preferenceAdjustedQuery = getPreferenceAdjustedQuery(query, catalogRequestSettings);
            const pageAndPreferenceAdjustedQuery = getExcludedPageAdjustedQuery(
                preferenceAdjustedQuery,
                catalogFilterExcludePageOneWhiteList,
            );

            const appliedFilters = getWhitelistedFilterObject(pageAndPreferenceAdjustedQuery, catalogFilterWhitelist);
            const dcOverride = rootState[SITE_SETTINGS_MODULE_NAME]?.inventoryDCPoolOverride;

            if (state.isPrePurchase) {
                pageAndPreferenceAdjustedQuery[CATALOG_QUERY_IN_STOCK] = true;
            }

            const catalogSlug = query?.curation || slug;

            return getCatalogItems({
                r15Svc: this.$r15Svc,
                catalogSlug,
                query: pageAndPreferenceAdjustedQuery,
                catalogFilterWhitelist,
                logger: this.$logger,
                dcOverride,
            })
                .then((res) => {
                    commit(SET_CATALOG_DATA, {
                        ...res,
                        slug,
                        query: pageAndPreferenceAdjustedQuery,
                        appliedFilters,
                    });
                })
                .catch((error) => {
                    this.$logger.debugError(`Failed to fetch catalog data: , ${error?.status} ${error?.statusText}`);
                    commit(SET_404_ERROR, { message: 'Failed to fetch catalog data' }, { root: true });
                });
        },
        [GET_CONTENTFUL_CONTENT]({ commit }, contentfulSlug) {
            return getContentfulPageContentForSlug({
                contentfulSvc: this.$contentfulSvc,
                contentType: CONTENT_TYPES.BROWSE_PAGE,
                slug: contentfulSlug,
                isTimed: true,
            })
                .then((res) => {
                    const contentfulContent = shapeBrowsePageContent(res, false);

                    commit(SET_CONTENTFUL_CONTENT, contentfulContent);
                })
                .catch((error) => {
                    commit(SET_CONTENTFUL_CONTENT, null);
                    this.$logger.warn(`Failed to fetch browse contentful content for ${contentfulSlug}: `, error);
                    return Promise.reject(error);
                });
        },
    },
    getters: {},
};
